import { TextInputField } from '@1debit/chimekit';
import type { ChangeEvent, FormEventHandler } from 'react';
import Button from 'components/shared/Buttons/BaseButton';
import { BUDGET_SECTIONS, INCOME_FIELD } from './constants';
import type { BudgetField, BudgetFieldErrors, BudgetFieldValues, BudgetSectionKey } from './types';

interface BudgetCalculatorFormProps {
  values: BudgetFieldValues;
  errors: BudgetFieldErrors;
  onFieldChange: (label: string, value: string) => void;
  onFieldFocus: (label: string) => void;
  onFieldBlur: (label: string) => void;
  onCalculate: () => void;
  onReset: () => void;
}

const sectionHintId = (key: BudgetSectionKey) => `budget-calculator-${key}-hint`;

export const BudgetCalculatorForm = ({
  values,
  errors,
  onFieldChange,
  onFieldFocus,
  onFieldBlur,
  onCalculate,
  onReset,
}: BudgetCalculatorFormProps) => {
  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  const fieldProps = (field: BudgetField) => ({
    label: field.label,
    hint: field.hint,
    inputMode: 'decimal' as const,
    value: values[field.label],
    error: errors[field.label],
    invalid: Boolean(errors[field.label]),
    'aria-invalid': Boolean(errors[field.label]),
    onChange: (event: ChangeEvent<HTMLInputElement>) => onFieldChange(field.label, event.target.value),
    onFocus: () => onFieldFocus(field.label),
    onBlur: () => onFieldBlur(field.label),
  });

  return (
    <form aria-label="Budget calculator" className="flex flex-col gap-8" onSubmit={handleSubmit}>
      {/* Native validation would pre-empt these custom rules. */}
      <TextInputField {...fieldProps(INCOME_FIELD)} aria-required />

      {BUDGET_SECTIONS.map(section => (
        <fieldset key={section.key} aria-describedby={sectionHintId(section.key)}>
          <legend className="text-heading-sm">{section.heading}</legend>
          <p id={sectionHintId(section.key)} className="mt-2 text-legal text-content-secondary">
            {section.hint}
          </p>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {section.fields.map(field => (
              <TextInputField key={field.label} {...fieldProps(field)} />
            ))}
          </div>
        </fieldset>
      ))}

      <div className="flex flex-col gap-4 sm:flex-row">
        <Button type="submit">Calculate</Button>
        <Button type="button" variant="outline" onClick={onReset}>
          Reset
        </Button>
      </div>
    </form>
  );
};
